import type {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import type { IPublicClientApplication } from '@azure/msal-browser';
import {
  BrowserCacheLocation,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../../environments/environment';

function loggerCallback(logLevel: LogLevel, message: string): void {
  if (logLevel <= LogLevel.Info) {
    console.log('[MSAL]', message);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  const { azureAd } = environment;
  return new PublicClientApplication({
    auth: {
      clientId: azureAd.clientId,
      authority: azureAd.authority,
      redirectUri: azureAd.redirectUri,
      postLogoutRedirectUri: azureAd.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const api = environment.urlAPI.replace(/\/?$/, '/');
  const scope = environment.azureAd.apiScope;
  const scopes = [scope];

  /**
   * No usar un único prefijo sobre toda la API: el widget público llamaba a ChatbotMessage / ChatbotChat
   * y MsalInterceptor intentaba acquireTokenSilent → error en iframe.
   * Entradas con valor `null` = recurso sin token (deben ir antes que `ChatbotChat/` si ambas coinciden).
   */
  const protectedResourceMap = new Map<string, Array<string> | null>();

  protectedResourceMap.set(`${api}ChatbotChat/GetAdditionalInfo/`, null);
  protectedResourceMap.set(`${api}ChatbotChat/GetFilters/`, null);
  protectedResourceMap.set(`${api}ChatbotChat/CheckAllowedOrigin/`, null);
  protectedResourceMap.set(`${api}ChatbotMessage/`, null);

  protectedResourceMap.set(`${api}ChatbotChat/`, scopes);

  protectedResourceMap.set(`${api}ChatbotClient/`, scopes);
  protectedResourceMap.set(`${api}ChatbotProject/`, scopes);
  protectedResourceMap.set(`${api}ChatbotDocument/`, scopes);
  protectedResourceMap.set(`${api}ChatbotDocumentCategory/`, scopes);
  protectedResourceMap.set(`${api}ChatbotApiClient/`, scopes);
  protectedResourceMap.set(`${api}ChatbotUser/`, scopes);
  protectedResourceMap.set(`${api}Statistics/`, scopes);
  protectedResourceMap.set(`${api}File/`, scopes);
  protectedResourceMap.set(`${api}DocumentIndexer/`, scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    strictMatching: false,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [environment.azureAd.apiScope],
    },
    loginFailedRoute: '/login-failed',
  };
}

export const MSAL_PROVIDERS = [
  { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
  { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
  { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
] as const;
